import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Plus,
  Settings2,
  Trash2,
  Sliders,
  ArrowUp,
  ArrowDown,
  X,
  Eye,
} from "lucide-react";
import {
  useEntityCustomFields,
  ENTITY_FIELD_TYPE_LABELS,
  type EntityFieldType,
  type EntityCustomField,
} from "./useEntityCustomFields";

interface DialogBaseProps {
  entity: "contact" | "company";
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

// ─────────────────────────────────────────────────────────────────────────────
// 1. ADD FIELD DIALOG
// ─────────────────────────────────────────────────────────────────────────────
export const AddEntityFieldDialog = ({
  entity,
  open,
  onOpenChange,
}: DialogBaseProps) => {
  const { addField } = useEntityCustomFields(entity);
  const entityLabel = entity === "contact" ? "Pessoa" : "Organização";

  const [name, setName] = useState("");
  const [type, setType] = useState<EntityFieldType>("text");
  const [options, setOptions] = useState<string[]>([]);
  const [newOptionInput, setNewOptionInput] = useState("");

  // 3 Visibility toggles
  const [showOnDealCreate, setShowOnDealCreate] = useState(true);
  const [showOnEntityCreate, setShowOnEntityCreate] = useState(true);
  const [showOnDealPanel, setShowOnDealPanel] = useState(true);

  useEffect(() => {
    if (open) {
      setName("");
      setType("text");
      setOptions([]);
      setNewOptionInput("");
      setShowOnDealCreate(true);
      setShowOnEntityCreate(true);
      setShowOnDealPanel(true);
    }
  }, [open]);

  const handleAddOption = () => {
    if (!newOptionInput.trim()) return;
    if (!options.includes(newOptionInput.trim())) {
      setOptions([...options, newOptionInput.trim()]);
    }
    setNewOptionInput("");
  };

  const handleRemoveOption = (idx: number) => {
    setOptions(options.filter((_, i) => i !== idx));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;

    addField({
      name: name.trim(),
      type,
      options: type === "select" ? options : undefined,
      visibility: {
        showOnDealCreate,
        showOnEntityCreate,
        showOnDealPanel,
      },
    });

    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <form onSubmit={handleSubmit} className="space-y-4">
          <DialogHeader>
            <DialogTitle className="text-base font-semibold flex items-center gap-2">
              <Plus className="w-4 h-4 text-primary" />
              Adicionar Campo de {entityLabel}
            </DialogTitle>
          </DialogHeader>

          {/* Nome */}
          <div className="space-y-1.5">
            <Label htmlFor="ent-cf-name" className="text-xs">
              Nome do Campo *
            </Label>
            <Input
              id="ent-cf-name"
              placeholder={`Ex: Segmento Secundário, Data de Aniversário...`}
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="h-8 text-xs"
              required
              autoFocus
            />
          </div>

          {/* Tipo */}
          <div className="space-y-1.5">
            <Label className="text-xs">Tipo de Campo</Label>
            <Select
              value={type}
              onValueChange={(val) => setType(val as EntityFieldType)}
            >
              <SelectTrigger className="h-8 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {(
                  Object.keys(ENTITY_FIELD_TYPE_LABELS) as EntityFieldType[]
                ).map((key) => (
                  <SelectItem key={key} value={key} className="text-xs">
                    {ENTITY_FIELD_TYPE_LABELS[key]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Opções de Seleção */}
          {type === "select" && (
            <div className="space-y-2 p-3 bg-muted/40 rounded-lg border border-border/60">
              <Label className="text-xs font-semibold">Opções de Seleção</Label>
              <div className="flex gap-1.5">
                <Input
                  placeholder="Nova opção..."
                  value={newOptionInput}
                  onChange={(e) => setNewOptionInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handleAddOption();
                    }
                  }}
                  className="h-7 text-xs flex-1"
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleAddOption}
                  className="h-7 text-xs"
                >
                  Adicionar
                </Button>
              </div>

              {options.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {options.map((opt, idx) => (
                    <span
                      key={idx}
                      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-card border border-border text-xs"
                    >
                      {opt}
                      <button
                        type="button"
                        onClick={() => handleRemoveOption(idx)}
                        className="hover:text-destructive text-muted-foreground"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Seção de Visibilidade (3 Opções) */}
          <div className="space-y-3 pt-3 border-t border-border/50">
            <div className="flex items-center gap-1.5">
              <Eye className="w-4 h-4 text-primary" />
              <Label className="text-xs font-semibold text-foreground">
                Regras de Visibilidade do Campo
              </Label>
            </div>

            {/* 1. Visualização de adição de deal */}
            <div className="flex items-start space-x-2.5 p-2.5 rounded-lg border border-border/40 bg-muted/20">
              <Checkbox
                id="cf-vis-deal-create"
                checked={showOnDealCreate}
                onCheckedChange={(c) => setShowOnDealCreate(Boolean(c))}
                className="mt-0.5"
              />
              <div className="space-y-0.5">
                <Label
                  htmlFor="cf-vis-deal-create"
                  className="text-xs font-medium cursor-pointer text-foreground block"
                >
                  Visualização de adição de deal
                </Label>
                <p className="text-[11px] text-muted-foreground leading-snug">
                  Se marcado, o campo ficará visível quando você adicionar um
                  novo lead ou negócio.
                </p>
              </div>
            </div>

            {/* 2. Visualização de adição de entidade */}
            <div className="flex items-start space-x-2.5 p-2.5 rounded-lg border border-border/40 bg-muted/20">
              <Checkbox
                id="cf-vis-ent-create"
                checked={showOnEntityCreate}
                onCheckedChange={(c) => setShowOnEntityCreate(Boolean(c))}
                className="mt-0.5"
              />
              <div className="space-y-0.5">
                <Label
                  htmlFor="cf-vis-ent-create"
                  className="text-xs font-medium cursor-pointer text-foreground block"
                >
                  Visualização de adição de {entityLabel}
                </Label>
                <p className="text-[11px] text-muted-foreground leading-snug">
                  Se marcado, o campo ficará visível quando você adicionar uma
                  nova {entityLabel.toLowerCase()}.
                </p>
              </div>
            </div>

            {/* 3. Visualização de deal (card do negócio) */}
            <div className="flex items-start space-x-2.5 p-2.5 rounded-lg border border-border/40 bg-muted/20">
              <Checkbox
                id="cf-vis-deal-panel"
                checked={showOnDealPanel}
                onCheckedChange={(c) => setShowOnDealPanel(Boolean(c))}
                className="mt-0.5"
              />
              <div className="space-y-0.5">
                <Label
                  htmlFor="cf-vis-deal-panel"
                  className="text-xs font-medium cursor-pointer text-foreground block"
                >
                  Visualização de deal (card do negócio)
                </Label>
                <p className="text-[11px] text-muted-foreground leading-snug">
                  Se marcado, o campo ficará visível nos detalhes do painel
                  lateral quando você abrir o card de um negócio.
                </p>
              </div>
            </div>
          </div>

          <DialogFooter className="pt-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => onOpenChange(false)}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              size="sm"
              className="bg-primary text-primary-foreground"
            >
              Salvar Campo
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// 2. EDIT FIELDS DIALOG
// ─────────────────────────────────────────────────────────────────────────────
export const EditEntityFieldsDialog = ({
  entity,
  open,
  onOpenChange,
}: DialogBaseProps) => {
  const { allFields, updateField, deleteField } = useEntityCustomFields(entity);
  const entityLabel = entity === "contact" ? "Pessoa" : "Organização";

  const [editingId, setEditingId] = useState<string | null>(null);
  const [name, setName] = useState("");
  const [showOnDealCreate, setShowOnDealCreate] = useState(true);
  const [showOnEntityCreate, setShowOnEntityCreate] = useState(true);
  const [showOnDealPanel, setShowOnDealPanel] = useState(true);

  const startEdit = (f: EntityCustomField) => {
    setEditingId(f.id);
    setName(f.name);
    setShowOnDealCreate(f.visibility.showOnDealCreate);
    setShowOnEntityCreate(f.visibility.showOnEntityCreate);
    setShowOnDealPanel(f.visibility.showOnDealPanel);
  };

  const handleSaveEdit = () => {
    if (!editingId || !name.trim()) return;
    updateField(editingId, {
      name: name.trim(),
      visibility: {
        showOnDealCreate,
        showOnEntityCreate,
        showOnDealPanel,
      },
    });
    setEditingId(null);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-base font-semibold flex items-center gap-2">
            <Settings2 className="w-4 h-4 text-primary" />
            Editar Campos de {entityLabel}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3 py-2">
          {allFields.map((f) => {
            const isSystem = f.id.startsWith("sys_");
            const isEditing = editingId === f.id;

            return (
              <div
                key={f.id}
                className="p-3 rounded-lg border border-border/70 bg-card/60 space-y-2.5"
              >
                {isEditing ? (
                  <div className="space-y-3">
                    <div className="space-y-1">
                      <Label className="text-xs">Nome do Campo</Label>
                      <Input
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        className="h-8 text-xs"
                      />
                    </div>

                    {/* 3 Visibility Toggles */}
                    <div className="space-y-2 pt-1 border-t border-border/40">
                      <Label className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">
                        Visibilidade
                      </Label>

                      <div className="flex items-start space-x-2">
                        <Checkbox
                          id={`edit-deal-create-${f.id}`}
                          checked={showOnDealCreate}
                          onCheckedChange={(c) =>
                            setShowOnDealCreate(Boolean(c))
                          }
                          className="mt-0.5"
                        />
                        <div className="space-y-0.5">
                          <Label
                            htmlFor={`edit-deal-create-${f.id}`}
                            className="text-xs cursor-pointer block font-medium"
                          >
                            Visualização de adição de deal
                          </Label>
                          <p className="text-[11px] text-muted-foreground">
                            Visível ao adicionar novo lead ou negócio
                          </p>
                        </div>
                      </div>

                      <div className="flex items-start space-x-2">
                        <Checkbox
                          id={`edit-ent-create-${f.id}`}
                          checked={showOnEntityCreate}
                          onCheckedChange={(c) =>
                            setShowOnEntityCreate(Boolean(c))
                          }
                          className="mt-0.5"
                        />
                        <div className="space-y-0.5">
                          <Label
                            htmlFor={`edit-ent-create-${f.id}`}
                            className="text-xs cursor-pointer block font-medium"
                          >
                            Visualização de adição de {entityLabel}
                          </Label>
                          <p className="text-[11px] text-muted-foreground">
                            Visível ao cadastrar nova{" "}
                            {entityLabel.toLowerCase()}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-start space-x-2">
                        <Checkbox
                          id={`edit-deal-panel-${f.id}`}
                          checked={showOnDealPanel}
                          onCheckedChange={(c) =>
                            setShowOnDealPanel(Boolean(c))
                          }
                          className="mt-0.5"
                        />
                        <div className="space-y-0.5">
                          <Label
                            htmlFor={`edit-deal-panel-${f.id}`}
                            className="text-xs cursor-pointer block font-medium"
                          >
                            Visualização de deal (card do negócio)
                          </Label>
                          <p className="text-[11px] text-muted-foreground">
                            Visível nos detalhes laterais do negócio
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center justify-end gap-2 pt-2 border-t border-border/40">
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => setEditingId(null)}
                        className="h-7 text-xs"
                      >
                        Cancelar
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        onClick={handleSaveEdit}
                        className="h-7 text-xs bg-primary text-primary-foreground"
                      >
                        Salvar
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-xs text-foreground truncate">
                          {f.name}
                        </span>
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground border border-border/50">
                          {ENTITY_FIELD_TYPE_LABELS[f.type] || f.type}
                        </span>
                        {isSystem && (
                          <span className="text-[9px] px-1 py-0.2 rounded bg-primary/10 text-primary font-medium">
                            Padrão
                          </span>
                        )}
                      </div>

                      {/* Badges de visibilidade atuais */}
                      <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
                        {f.visibility.showOnDealCreate && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-500/10 text-blue-600 dark:text-blue-400 font-medium">
                            + Deal
                          </span>
                        )}
                        {f.visibility.showOnEntityCreate && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 font-medium">
                            + {entityLabel}
                          </span>
                        )}
                        {f.visibility.showOnDealPanel && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-purple-500/10 text-purple-600 dark:text-purple-400 font-medium">
                            Card Deal
                          </span>
                        )}
                        {!f.visibility.showOnDealCreate &&
                          !f.visibility.showOnEntityCreate &&
                          !f.visibility.showOnDealPanel && (
                            <span className="text-[10px] text-muted-foreground italic">
                              Oculto em todas as telas
                            </span>
                          )}
                      </div>
                    </div>

                    <div className="flex items-center gap-1.5 shrink-0">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => startEdit(f)}
                        className="h-7 px-2 text-xs"
                      >
                        Editar
                      </Button>
                      {!isSystem && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => deleteField(f.id)}
                          className="h-7 px-2 text-xs text-destructive hover:bg-destructive/10"
                          title="Excluir campo personalizado"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => onOpenChange(false)}
          >
            Fechar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// 3. REORDER FIELDS DIALOG
// ─────────────────────────────────────────────────────────────────────────────
export const ReorderEntityFieldsDialog = ({
  entity,
  open,
  onOpenChange,
}: DialogBaseProps) => {
  const { allFields, reorderFields } = useEntityCustomFields(entity);
  const entityLabel = entity === "contact" ? "Pessoa" : "Organização";
  const [localList, setLocalList] = useState<EntityCustomField[]>([]);

  useEffect(() => {
    if (open) {
      setLocalList([...allFields]);
    }
  }, [open, allFields]);

  const moveUp = (index: number) => {
    if (index === 0) return;
    const next = [...localList];
    const temp = next[index - 1];
    next[index - 1] = next[index];
    next[index] = temp;
    setLocalList(next);
  };

  const moveDown = (index: number) => {
    if (index === localList.length - 1) return;
    const next = [...localList];
    const temp = next[index + 1];
    next[index + 1] = next[index];
    next[index] = temp;
    setLocalList(next);
  };

  const handleSaveOrder = () => {
    reorderFields(localList);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-base font-semibold flex items-center gap-2">
            <Sliders className="w-4 h-4 text-primary" />
            Reordenar Campos de {entityLabel}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-1.5 py-3 max-h-[60vh] overflow-y-auto">
          {localList.map((f, index) => (
            <div
              key={f.id}
              className="flex items-center justify-between p-2 rounded-lg border border-border/70 bg-card/60 text-xs"
            >
              <div className="flex items-center gap-2 min-w-0">
                <span className="font-mono text-muted-foreground w-4 text-center">
                  {index + 1}.
                </span>
                <span className="font-medium truncate">{f.name}</span>
                <span className="text-[10px] text-muted-foreground">
                  ({ENTITY_FIELD_TYPE_LABELS[f.type] || f.type})
                </span>
              </div>

              <div className="flex items-center gap-1 shrink-0">
                <button
                  type="button"
                  disabled={index === 0}
                  onClick={() => moveUp(index)}
                  className="p-1 rounded hover:bg-muted disabled:opacity-30 cursor-pointer"
                  title="Mover para cima"
                >
                  <ArrowUp className="w-3.5 h-3.5" />
                </button>
                <button
                  type="button"
                  disabled={index === localList.length - 1}
                  onClick={() => moveDown(index)}
                  className="p-1 rounded hover:bg-muted disabled:opacity-30 cursor-pointer"
                  title="Mover para baixo"
                >
                  <ArrowDown className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          ))}
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => onOpenChange(false)}
          >
            Cancelar
          </Button>
          <Button
            type="button"
            size="sm"
            onClick={handleSaveOrder}
            className="bg-primary text-primary-foreground"
          >
            Salvar Ordem
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
